<template>
  <div class="category-page">
    <NavBar />
    
    <!-- 面包屑导航 -->
    <div class="breadcrumb-container">
      <a-breadcrumb>
        <a-breadcrumb-item><a href="/">首页</a></a-breadcrumb-item>
        <a-breadcrumb-item>商品分类</a-breadcrumb-item>
      </a-breadcrumb>
    </div>
    
    <div class="category-content">
      <!-- 商品分类标题 -->
      <h1 class="page-title">商品分类</h1>
      
      <!-- 分类选项卡 - 骨架屏 -->
      <div v-if="categories.length === 0" class="category-tabs-skeleton">
        <a-skeleton :animation="true">
          <a-skeleton-line :rows="1" :widths="['80%']" :line-height="50" />
        </a-skeleton>
      </div>
      
      <!-- 分类选项卡 -->
      <div v-else class="category-tabs">
        <div 
          v-for="category in categories" 
          :key="category.id"
          :class="['category-tab', { active: currentCategoryId === category.id }]"
          @click="selectCategory(category.id)"
        >
          {{ category.name }}
        </div>
      </div>
      
      <!-- 当前分类热门推荐标题 -->
      <div v-if="currentCategory" class="category-section-title">
        {{ currentCategory.name }}热门推荐
      </div>
      
      <!-- 商品列表 - 骨架屏 -->
      <div v-if="loading" class="product-grid">
        <div class="product-card" v-for="i in 8" :key="'skeleton-' + i">
          <div class="product-card-inner">
            <a-skeleton :animation="true">
              <a-skeleton-shape :style="{ width: '100%', height: '280px' }" shape="square" />
              <div :style="{ padding: '16px' }">
                <a-skeleton-line :rows="2" :widths="['100%', '60%']" />
                <a-skeleton-line :rows="1" :widths="['40%']" :style="{ marginTop: '12px' }" />
                <a-skeleton-shape :style="{ width: '100%', height: '40px', marginTop: '16px' }" shape="square" />
              </div>
            </a-skeleton>
          </div>
        </div>
      </div>
      
      <!-- 商品列表 -->
      <div v-else class="product-grid">
        <div class="product-card" v-for="product in currentProducts" :key="product.id" @click="$router.push(`/product/${product.id}`)">
          <div class="product-card-inner">
            <div class="product-image">
              <img :src="product.cover_url" :alt="product.name">
              <div v-if="product.original_price && product.price < product.original_price" class="discount-badge">
                优惠
              </div>
            </div>
            <div class="product-info">
              <h3 class="product-name">{{ product.name }}</h3>
              <div class="price-box">
                <span class="product-price">¥{{ product.price }}</span>
                <span v-if="product.original_price && product.price < product.original_price" class="original-price">¥{{ product.original_price }}</span>
              </div>
              <div class="product-rating">
                <a-rate :default-value="5" disabled size="small" />
                <span class="rating-count">({{ product.sales }})</span>
              </div>
              <a-button type="primary" class="add-cart-btn">加入购物车</a-button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-if="!loading && currentProducts.length === 0" class="empty-state">
        <p>暂无商品</p>
      </div>
    </div>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { getAllCategories } from '@/api/index.js'

export default {
  name: 'CategoryPage',
  components: {
    NavBar,
    Footer
  },
  data() {
    return {
      categories: [],
      currentCategoryId: null,
      loading: false
    }
  },
  computed: {
    currentCategory() {
      return this.categories.find(cat => cat.id === this.currentCategoryId) || null
    },
    currentProducts() {
      if (!this.currentCategory || !this.currentCategory.goods) {
        return []
      }
      return this.currentCategory.goods
    }
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      this.loading = true
      try {
        const result = await getAllCategories()
        if (result.code === 1 && result.data) {
          this.categories = result.data
          // 默认选中第一个分类
          if (this.categories.length > 0) {
            this.currentCategoryId = this.categories[0].id
          }
        }
      } catch (error) {
        console.error('加载分类失败:', error)
      } finally {
        this.loading = false
      }
    },
    selectCategory(categoryId) {
      this.currentCategoryId = categoryId
    }
  }
}
</script>

<style scoped>
.category-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.breadcrumb-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 40px;
}

.category-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px 60px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1d2129;
  text-align: center;
  margin-bottom: 40px;
}

/* 分类选项卡骨架屏 */
.category-tabs-skeleton {
  margin-bottom: 40px;
}

/* 分类选项卡 */
.category-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 40px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.category-tab {
  flex-shrink: 0;
  padding: 15px 30px;
  font-size: 16px;
  color: #666;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e5e6eb;
  border-right: none;
}

.category-tab:first-child {
  border-radius: 8px 0 0 8px;
}

.category-tab:last-child {
  border-radius: 0 8px 8px 0;
  border-right: 1px solid #e5e6eb;
}

.category-tab:hover {
  color: #ff1493;
  background: rgba(255, 105, 180, 0.05);
}

.category-tab.active {
  color: #fff;
  background: linear-gradient(135deg, #ff69b4, #ff1493);
  border-color: #ff1493;
  position: relative;
  z-index: 1;
}

/* 分类热门推荐标题 */
.category-section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1d2129;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #ff69b4;
  display: inline-block;
}

/* 商品网格 */
.product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 30px;
}

.product-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  padding: 2px;
  background: #e5e6eb;
  cursor: pointer;
}

.product-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px;
  background: conic-gradient(from 0deg at 50% 50%, #ff69b4, #ff1493, #ff69b4, #ff1493, #ff69b4);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-card:hover::before {
  opacity: 1;
  animation: borderRotate 3s linear infinite;
}

.product-card-inner {
  background: #ffffff;
  border-radius: 10px;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

@keyframes borderRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.product-image {
  width: 100%;
  height: 280px;
  overflow: hidden;
  background: #f5f5f5;
  position: relative;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.discount-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: linear-gradient(135deg, #ff6b6b, #ff1493);
  color: #fff;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.product-info {
  padding: 16px;
}

.product-name {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 8px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
  min-height: 48px;
}

.price-box {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}

.product-price {
  font-size: 20px;
  font-weight: 700;
  color: #ff1493;
  text-align: left;
}

.original-price {
  font-size: 14px;
  color: #999;
  text-decoration: line-through;
}

.product-rating {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.rating-count {
  font-size: 12px;
  color: #999;
}

.add-cart-btn {
  width: 100%;
  padding: 0 20px;
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: none !important;
  box-shadow: none !important;
  background: linear-gradient(90deg, #ff69b4, #ff1493) !important;
  color: #fff !important;
  outline: none !important;
}

.add-cart-btn:hover {
  background: linear-gradient(90deg, #ff1493, #ff69b4) !important;
}

.add-cart-btn:deep(.arco-btn) {
  border: none !important;
  box-shadow: none !important;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #999;
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .product-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .category-tabs {
    gap: 10px;
  }
  
  .category-tab {
    padding: 10px 20px;
    font-size: 14px;
  }
}
</style>
